// Part of the agent UI for spark#246 / @adr SPARK-0034-ai-agents-are-project-context-not-team-members@2: an AI agent is
// PROJECT CONTEXT, never a team member. This is the choose-agents list, not a candidate search.

import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { TextInput } from "@/components/design/TextField";
import { useDebounced } from "@/lib/use-debounced";
import { userErrorMessage } from "@/lib/user-error";
import { AgentRow } from "./AgentRow";
import { agentPath, agentText } from "./agents";
import type { AgentListRow } from "./agents";
import { useAgentSearch } from "./use-agent-search";

const PAGE_SIZE = 24;

/**
 * The instance's agents, a page at a time, with a search box on top the way the candidate search
 * works: debounced at 250ms, a result count while searching, one line when nothing matches, and
 * "Load more" for the next page. Without a query genesis lists every agent by label; with one it
 * searches name, role, tools, integrations and guardrails, with a trigram fallback for typos. The
 * Added state comes from the match, so it survives any query and any page.
 *
 * `onRowsLoaded` hands every page that arrives to the picker, which matches assistant answers
 * against the agents it has seen (there is no full list to match against any more).
 */
export function AgentCatalogueList({
  isSelected,
  onToggle,
  onRowsLoaded,
}: {
  isSelected: (agent: AgentListRow) => boolean;
  onToggle: (agent: AgentListRow) => void;
  onRowsLoaded?: (agents: readonly AgentListRow[]) => void;
}) {
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const debounced = useDebounced(query, 250);
  const searching = debounced.trim().length > 0;
  const search = useAgentSearch({ query: debounced, pageSize: PAGE_SIZE });
  const shown = search.nodes;
  useEffect(() => {
    if (shown.length > 0) onRowsLoaded?.(shown);
  }, [shown, onRowsLoaded]);

  return (
    // Fixed height: the dialog must not resize while the result list changes under a query.
    <div className="flex h-[min(70vh,44rem)] flex-col gap-3">
      <TextInput
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={intl.formatMessage({ id: "match.agent.searchLabel", defaultMessage: "Search agents" })}
        placeholder={intl.formatMessage({ id: "match.agent.searchPlaceholder", defaultMessage: "Name, role, tool …" })}
      />
      <div className="smycles-field-meta min-h-[1.25rem] shrink-0 text-ink-muted" role="status">
        {search.error ? (
          <span role="alert" className="text-ink">{userErrorMessage(intl, search.error)}</span>
        ) : search.loading ? (
          searching ? (
            <FormattedMessage id="search.searching" defaultMessage="Searching …" />
          ) : (
            <FormattedMessage id="common.loading" defaultMessage="Loading…" />
          )
        ) : !searching ? null : shown.length === 0 ? (
          <FormattedMessage id="search.noResults" defaultMessage="No matches" />
        ) : search.hasMore ? (
          // A lower bound while more pages remain: genesis says only that there is a next page.
          <FormattedMessage id="match.agent.searchCountMore" defaultMessage="{count}+ agents" values={{ count: shown.length }} />
        ) : (
          <FormattedMessage
            id="match.agent.searchCount"
            defaultMessage="{count, plural, one {# agent} other {# agents}}"
            values={{ count: shown.length }}
          />
        )}
      </div>
      <div className="-mx-1 min-h-0 flex-1 space-y-2 overflow-y-auto px-1 pb-1">
        {/* First load: placeholder rows in AgentRow's shape, so the dialog opens at its final size. */}
        {search.loading && shown.length === 0 && !search.error
          ? Array.from({ length: 6 }, (_, i) => (
              <div key={i} aria-hidden className="box-flat bg-page flex items-start gap-3 p-3">
                <span className="h-10 w-10 shrink-0 animate-pulse bg-ink/[0.08]" />
                <div className="flex-1 space-y-2 pt-1">
                  <div className="h-3 w-1/4 animate-pulse bg-ink/[0.08]" />
                  <div className="h-3 w-3/4 animate-pulse bg-ink/[0.06]" />
                </div>
              </div>
            ))
          : null}
        {shown.map((entry) => {
          const selected = isSelected(entry);
          return (
            <AgentRow
              key={entry.id}
              name={entry.name}
              role={agentText(entry.role)}
              to={agentPath(entry.id)}
              action={
                <Button variant={selected ? "ghost" : "secondary"} size="sm" onClick={() => onToggle(entry)}>
                  {selected ? (
                    <FormattedMessage id="match.agent.added" defaultMessage="Hinzugefügt" />
                  ) : (
                    <FormattedMessage id="match.agent.add" defaultMessage="Hinzufügen" />
                  )}
                </Button>
              }
            />
          );
        })}
        {search.hasMore ? (
          <div className="pt-1 text-center">
            <Button variant="secondary" size="sm" disabled={search.loadingMore} onClick={() => search.loadMore()}>
              <FormattedMessage id="common.loadMore" defaultMessage="Load more" />
            </Button>
            {search.loadMoreFailed ? (
              <div role="alert" className="smycles-field-meta mt-1 text-ink">
                <FormattedMessage id="match.agent.loadMoreFailed" defaultMessage="The next agents could not be loaded." />
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}
